/**
 * ReviewConfirmStep.js
 *
 * Step 3 of the clearance wizard.
 * Displays a read-only summary of selected items for review.
 * Allows user to confirm selections (which locks them and proceeds to payment).
 *
 * IMPORTANT: Once confirmed, user cannot go back to modify selections.
 *
 * @component
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Typography,
  Divider,
  Alert,
  Modal,
  Descriptions,
  Tag,
} from "antd";
import {
  ArrowLeftOutlined,
  LockOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import Utilities from "../../../../dependencies/custom/react-utilities";
import domainSettings from "../../../../dependencies/custom/settings";
import { message } from "antd";

const { Text, Title } = Typography;
const { confirm } = Modal;

/**
 * ReviewConfirmStep Component
 *
 * @param {object} clearance - The clearance data with selections
 * @param {function} onNext - Callback to proceed to payment step
 * @param {function} onPrevious - Callback to go back to item selection
 * @param {function} onRefresh - Callback to refresh clearance data
 * @param {boolean} canGoBack - Whether back navigation is allowed
 */
const ReviewConfirmStep = ({
  clearance,
  onNext,
  onPrevious,
  onRefresh,
  canGoBack,
}) => {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  /**
   * Table columns for selected items
   */
  const columns = [
    {
      title: "Item",
      dataIndex: "item_name",
      key: "item_name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Brand",
      dataIndex: "brand_name",
      key: "brand_name",
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: "Size",
      dataIndex: "size_name",
      key: "size_name",
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      align: "right",
      render: (price) => `UGX ${parseFloat(price || 0).toLocaleString()}`,
    },
    {
      title: "Total",
      dataIndex: "total_price",
      key: "total_price",
      align: "right",
      render: (price) => (
        <Text strong>UGX {parseFloat(price || 0).toLocaleString()}</Text>
      ),
    },
  ];

  /**
   * Prepare table data from selections
   */
  const tableData =
    clearance?.selections?.map((sel, index) => ({
      key: sel.id || index,
      ...sel,
      total_price: (sel.unit_price || 0) * (sel.quantity || 0),
    })) || [];

  /**
   * Calculate grand total
   */
  const grandTotal = tableData.reduce(
    (sum, item) => sum + (item.total_price || 0),
    0,
  );

  /**
   * Handle confirmation with confirmation dialog
   */
  const handleConfirm = () => {
    confirm({
      title: "Confirm Your Selections?",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <Alert
            message="This action cannot be undone"
            description="Once you confirm, you will not be able to modify your item selections. You will proceed to the payment step."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Total Items">
              {tableData.length}
            </Descriptions.Item>
            <Descriptions.Item label="Grand Total">
              <Text strong style={{ color: "#1890ff" }}>
                UGX {grandTotal.toLocaleString()}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </div>
      ),
      okText: "Yes, Confirm Selections",
      okType: "primary",
      cancelText: "Cancel",
      width: 500,
      async onOk() {
        await confirmSelections();
      },
    });
  };

  /**
   * Submit confirmation to API
   */
  const confirmSelections = async () => {
    setConfirming(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "PUT",
        domainSettings.urls.bot.portalSelection,
        { botpc: clearance.botpc },
        navigate,
      );

      if (response.ok) {
        message.success(
          "Selections confirmed successfully. Proceeding to payment...",
        );
        await onRefresh();
        onNext();
      } else {
        Utilities.showError(response, "Failed to confirm selections");
      }
    } catch (error) {
      console.error("Confirmation error:", error);
      message.error("An error occurred while confirming selections");
    } finally {
      setConfirming(false);
    }
  };

  if (!clearance) return null;

  // Check if already confirmed
  const isConfirmed = [
    "Pending Payment",
    "Partially Paid",
    "Paid",
    "Ready for Pickup",
    "Cleared",
  ].includes(clearance.status);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>
          <CheckCircleOutlined style={{ marginRight: 8 }} />
          Review & Confirm Selections
        </Title>
        <Text type="secondary">
          {isConfirmed
            ? "Your selections have been confirmed. Proceed to payment."
            : "Please review your selections carefully before confirming."}
        </Text>
      </div>

      {/* Warning for unconfirmed */}
      {!isConfirmed && (
        <Alert
          message="Important: Review Carefully"
          description="Once you confirm your selections, you will NOT be able to make changes. The total amount shown will be your final payment amount."
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Success for confirmed */}
      {isConfirmed && (
        <Alert
          message="Selections Confirmed"
          description="Your item selections have been locked. Please proceed to complete your payment."
          type="success"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Student & Assignment Info */}
      <Descriptions
        bordered
        column={{ xs: 1, sm: 2 }}
        size="small"
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label="Student">
          {clearance.student_name}
        </Descriptions.Item>
        <Descriptions.Item label="BOTPC">
          <Text code>{clearance.botpc}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={isConfirmed ? "green" : "blue"}>{clearance.status}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Academic Year">
          {clearance.acadyr}
        </Descriptions.Item>
      </Descriptions>

      <Divider>Selected Items</Divider>

      {/* Items Table */}
      {tableData.length > 0 ? (
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          scroll={{ x: 600 }}
          style={{ marginBottom: 24 }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5}>
                  <Text strong>Grand Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong style={{ fontSize: 18, color: "#1890ff" }}>
                    UGX {grandTotal.toLocaleString()}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      ) : (
        <Alert
          message="No Items Selected"
          description="Please go back and select at least one item."
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Payment Summary */}
      <div
        style={{
          padding: 20,
          background: isConfirmed ? "#f6ffed" : "#fff7e6",
          borderRadius: 8,
          border: `1px solid ${isConfirmed ? "#b7eb8f" : "#ffd591"}`,
          marginBottom: 24,
        }}
      >
        <Title level={5} style={{ marginBottom: 16 }}>
          {isConfirmed ? "Payment Summary" : "Amount to Pay After Confirmation"}
        </Title>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text>Total Amount:</Text>
            <Text strong style={{ fontSize: 20 }}>
              UGX{" "}
              {parseFloat(
                clearance.total_amount || grandTotal,
              ).toLocaleString()}
            </Text>
          </div>
          {isConfirmed && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>Amount Paid:</Text>
                <Text type="success">
                  UGX {parseFloat(clearance.amount_paid || 0).toLocaleString()}
                </Text>
              </div>
              <Divider style={{ margin: "8px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text strong>Balance Due:</Text>
                <Text
                  strong
                  style={{ fontSize: 20 }}
                  type={
                    parseFloat(clearance.balance) > 0 ? "danger" : "success"
                  }
                >
                  UGX {parseFloat(clearance.balance || 0).toLocaleString()}
                </Text>
              </div>
            </>
          )}
        </Space>
      </div>

      {/* Navigation Buttons */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        {!isConfirmed && canGoBack ? (
          <Button size="large" icon={<EditOutlined />} onClick={onPrevious}>
            Edit Selections
          </Button>
        ) : (
          <Button
            size="large"
            icon={<ArrowLeftOutlined />}
            onClick={onPrevious}
            disabled={!canGoBack}
          >
            Back
          </Button>
        )}

        {isConfirmed ? (
          <Button type="primary" size="large" onClick={onNext}>
            Proceed to Payment
          </Button>
        ) : (
          <Button
            type="primary"
            size="large"
            icon={<LockOutlined />}
            onClick={handleConfirm}
            loading={confirming}
            disabled={tableData.length === 0}
            danger
          >
            Confirm Selections
          </Button>
        )}
      </div>
    </div>
  );
};

export default ReviewConfirmStep;
